'use client';

import { useEffect, useState } from 'react';
import type { TickerItem } from '@/lib/event-filters';
import { getEventColor } from '@/lib/constants';
import { getEventSlug } from '@/lib/event-slugs';
import { Flag } from '@/components/ui/Flag';
import { uiFb } from '@/lib/i18n-helpers';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface WorldEventsTickerProps {
  items: TickerItem[];
  localItems?: TickerItem[];
  lang?: string;
  /**
   * The "today" reference (YYYY-MM-DD) used for the countdown badges, supplied
   * by the server so SSR and the first client render agree. Recomputing
   * `new Date()` here at render time made the server HTML (build/request date)
   * disagree with the client hydration (viewer date), producing a text-content
   * hydration mismatch (React #418). After mount we refresh it to the viewer's
   * live date so countdowns stay accurate on stale static builds. (almanak-isqx)
   * Optional: falls back to the earliest item's date (a deterministic, render-
   * stable value) when a caller has no server date to hand.
   */
  referenceDate?: string;
}

function daysBetween(from: string, to: string): number {
  const a = new Date(from + 'T00:00:00');
  const b = new Date(to + 'T00:00:00');
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

function countdownText(days: number, lang: string): string {
  if (days === 0) return uiFb('ui.ticker.today', 'Today', lang);
  if (days === 1) return uiFb('ui.ticker.tomorrow', 'Tmrw', lang);
  return uiFb('ui.ticker.in_days', 'In {n}d', lang, { n: days });
}

function countdownClass(days: number): string {
  if (days === 0) return 'bg-semantic-error/12 text-semantic-error font-bold';
  if (days === 1) return 'bg-semantic-warning/12 text-semantic-warning font-bold';
  return 'bg-surface-hover text-text-secondary font-medium';
}

function TickerEntry({ item, referenceDate, lang, useLocalTitle }: { item: TickerItem; referenceDate: string; lang: string; useLocalTitle?: boolean }) {
  const d = new Date(item.dateStart + 'T00:00:00');
  const month = MONTH_ABBR[d.getMonth()];
  const day = d.getDate();
  const days = daysBetween(referenceDate, item.dateStart);
  const slug = getEventSlug(item.eventId);
  const href = slug ? `/events/${slug}` : undefined;
  const displayTitle = useLocalTitle ? (item.titleLocal ?? item.title) : item.title;

  const MAX_FLAGS = 5;
  const shownFlags = item.countries.slice(0, MAX_FLAGS);
  const overflow = item.countries.length - MAX_FLAGS;

  const inner = (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span
        className="h-1.5 w-1.5 rounded-full shrink-0"
        style={{ backgroundColor: getEventColor(item.type) }}
        aria-hidden="true"
      />
      {/* Flags inline */}
      <span className="flex items-center gap-px shrink-0">
        {shownFlags.map(code => (
          <Flag key={code} code={code} size={14} />
        ))}
        {overflow > 0 && (
          <span className="text-caption text-muted ml-0.5">+{overflow}</span>
        )}
      </span>
      <span className="text-label font-semibold text-text-primary">{displayTitle}</span>
      <span className="text-caption tabular-nums text-muted">{month} {day}</span>
      <span className={`inline-flex items-center rounded px-1 py-px text-caption uppercase leading-none ${countdownClass(days)}`}>
        {countdownText(days, lang)}
      </span>
    </span>
  );

  if (href) {
    return (
      <a
        href={href}
        className="hover:bg-surface-hover/50 rounded px-1.5 py-0.5 transition-colors"
        aria-label={`${item.title}, ${item.countries.join(' ')}, ${month} ${day}`}
      >
        {inner}
      </a>
    );
  }

  return <span className="px-1.5 py-0.5">{inner}</span>;
}

export function WorldEventsTicker({ items, localItems, lang = 'en', referenceDate: serverDate }: WorldEventsTickerProps) {
  // Start from the server-provided date so the first client render matches SSR
  // (no hydration mismatch), then swap to the viewer's live date after mount so
  // countdowns are correct even on statically-built pages served days later.
  const [referenceDate, setReferenceDate] = useState(serverDate ?? items[0]?.dateStart ?? '');
  useEffect(() => {
    setReferenceDate(new Date().toISOString().slice(0, 10));
  }, []);

  if (!items || items.length === 0) return null;

  const hasLocal = localItems && localItems.length > 0;

  // Duplicate for seamless infinite scroll
  const doubled = [...items, ...items];
  const doubledLocal = hasLocal ? [...localItems, ...localItems] : [];

  return (
    <div
      className="ticker-hover fixed left-0 right-0 top-14 z-40 border-b border-border-subtle bg-surface-elevated/95 backdrop-blur-sm overflow-hidden"
      aria-label={uiFb('ui.ticker.title', 'World Events', lang)}
      role="marquee"
    >
      <div className="animate-flag-scroll flex items-center gap-5 py-0.5" style={{ width: 'max-content' }}>
        {doubled.map((item, i) => (
          <TickerEntry
            key={`${item.eventId}-${i}`}
            item={item}
            referenceDate={referenceDate}
            lang={lang}
          />
        ))}
      </div>
      {hasLocal && (
        <div className="animate-flag-scroll flex items-center gap-5 py-0.5 border-t border-border-subtle/50" style={{ width: 'max-content' }}>
          {doubledLocal.map((item, i) => (
            <TickerEntry
              key={`local-${item.eventId}-${i}`}
              item={item}
              referenceDate={referenceDate}
              lang={lang}
              useLocalTitle
            />
          ))}
        </div>
      )}
    </div>
  );
}
